import { setPaymentProcessingError } from '~/actions/billing.actions';
import { ActionKeys } from '~/actions/constants';
import * as gtmActions from '~/actions/gtm.actions';
import { sendTrackEvent } from '~/actions/prod-analytics.actions';
import * as stripeActions from '~/actions/stripe.actions';
import { updateCreditCard, updateUserDefaultAddress } from '~/actions/user.actions';
import axios, { AxiosResponse } from 'axios';
import { SET_FIRST_CREDIT_CARD } from '~/constants/gtm';
import { LocationDescriptorObject } from 'history';
import { Action } from 'redux';
import { channel } from 'redux-saga';
import { call, put, take, takeLatest } from 'redux-saga/effects';
import { getLocation } from '~/selectors/router.selectors';
import { getUserAddress, isStripeAuthorized } from '~/selectors/user.selectors';
import { createApiAddress, editApiAddress } from '~/services/address.service';
import { sanitizeAddress } from '~/types/Address';
import APIAddress, { fromJSON, JSONAPIAddress, toJSON } from '~/types/APIAddress';
import StripeClient, { CreateTokenResult, StripeElement, StripeEvent } from '~/types/StripeClient';
import { selectState } from '..';
import { requestUpdatedTrack, trackPaymentMethodUpdate } from '~/utils/TrackingHelper/utils';
import { PAYMENT_METHOD_EVENT } from '~/utils/TrackingHelper/types';

const STRIPE_JS_URL = 'https://js.stripe.com/v3/';

// Using this file as singleton
let stripeClient: Maybe<StripeClient> = null;
const stripeElements: { [elementId: string]: StripeElement } = {};

// Easy to mock functions, just that
export const getStripeClient = () => stripeClient;
export const getStripeElements = () => stripeElements;

const stripeChannel = channel();

export function* loadStripe(action: ReturnType<typeof stripeActions.loadStripe>) {
  try {
    const stripeJs = document.createElement('script');
    stripeJs.src = STRIPE_JS_URL;
    stripeJs.async = true;
    stripeJs.onload = () => {
      stripeClient = window.Stripe(action.stripePublishableKey);
      stripeChannel.put(stripeActions.loadStripeSuccess());
    };

    document.body.appendChild(stripeJs);
  } catch (e) {
    yield put(stripeActions.loadStripeError(e.toString()));
  }
}

export function* createElement(action: ReturnType<typeof stripeActions.createElement>) {
  const stripeClient: Maybe<StripeClient> = yield call(getStripeClient);
  try {
    if (stripeClient) {
      const { numberElementId, expElementId, cvcElementId } = action.payload;

      const elements = stripeClient.elements();

      const numberElem = elements.create('cardNumber', action.payload.cardNumberOptions);
      const expElem = elements.create('cardExpiry', action.payload.cardExpOptions);
      const cvcElem = elements.create('cardCvc', action.payload.cardCvcOptions);

      numberElem.mount(`#${numberElementId}`);
      numberElem.addEventListener('change', (event: StripeEvent) => {
        stripeChannel.put(stripeActions.onStripeElementChange(numberElementId, event));

        if (event.error) {
          stripeChannel.put(stripeActions.setStripeFormError(numberElementId, event.error.message));
        } else if (event.empty) {
          stripeChannel.put(stripeActions.setStripeElementEmpty(numberElementId, true));
        } else {
          stripeChannel.put(stripeActions.clearStripeFormError(numberElementId));
          stripeChannel.put(stripeActions.setStripeElementEmpty(numberElementId, false));
        }
      });

      expElem.mount(`#${expElementId}`);
      expElem.addEventListener('change', (event: StripeEvent) => {
        stripeChannel.put(stripeActions.onStripeElementChange(expElementId, event));

        if (event.error) {
          stripeChannel.put(stripeActions.setStripeFormError(expElementId, event.error.message));
        } else if (event.empty) {
          stripeChannel.put(stripeActions.setStripeElementEmpty(expElementId, true));
        } else {
          stripeChannel.put(stripeActions.clearStripeFormError(expElementId));
          stripeChannel.put(stripeActions.setStripeElementEmpty(expElementId, false));
        }
      });
      cvcElem.mount(`#${cvcElementId}`);
      cvcElem.addEventListener('change', (event: StripeEvent) => {
        stripeChannel.put(stripeActions.onStripeElementChange(cvcElementId, event));

        if (event.error) {
          stripeChannel.put(stripeActions.setStripeFormError(cvcElementId, event.error.message));
        } else if (event.empty) {
          stripeChannel.put(stripeActions.setStripeElementEmpty(cvcElementId, true));
        } else {
          stripeChannel.put(stripeActions.clearStripeFormError(cvcElementId));
          stripeChannel.put(stripeActions.setStripeElementEmpty(cvcElementId, false));
        }
      });

      stripeElements[numberElementId] = numberElem;
      stripeElements[expElementId] = expElem;
      stripeElements[cvcElementId] = cvcElem;
      yield put(stripeActions.createElementSuccess(numberElementId));
      yield put(stripeActions.createElementSuccess(expElementId));
      yield put(stripeActions.createElementSuccess(cvcElementId));
    } else {
      yield put(
        stripeActions.createElementError(
          action.payload.numberElementId,
          'Trying to create an element when stripe is not loaded',
        ),
      );
    }
  } catch (e) {
    yield put(stripeActions.createElementError(action.payload.numberElementId, e.toString()));
  }
}

export function* createToken(action: ReturnType<typeof stripeActions.createStripeToken>) {
  const {
    analyticsContext,
    elementId,
    billingAddress,
    updatePrimaryAddress,
    trackingReference,
    creditDebitSelectionValue,
  } = action.payload;
  const stripeClient: StripeClient = yield call(getStripeClient);

  trackPaymentMethodUpdate(trackingReference, PAYMENT_METHOD_EVENT.operations.SAVE_ATTEMPTED, {});

  try {
    if (stripeClient) {
      const stripeElements: Record<string, StripeElement> = yield call(getStripeElements);
      const element = stripeElements[action.payload.elementId];

      const location: LocationDescriptorObject =
        yield selectState<LocationDescriptorObject>(getLocation);
      if (element) {
        if (updatePrimaryAddress) {
          // Address Creation and Validation (validation only takes place when users save a new APIAddress)
          const billingAPIAddressJSON: JSONAPIAddress = toJSON(sanitizeAddress(billingAddress))!;
          billingAPIAddressJSON.is_default_sender = true;

          const userAddress: Maybe<APIAddress> =
            yield selectState<Maybe<APIAddress>>(getUserAddress);

          if (userAddress) {
            if (userAddress.id === billingAPIAddressJSON.id) {
              const response: AxiosResponse<JSONAPIAddress> = yield call(
                editApiAddress,
                billingAPIAddressJSON.id!,
                billingAPIAddressJSON,
              );
              const address = fromJSON(response.data)!; // If the Address provided is invalid, this will error out and processingPaymentError will be set
              yield put(updateUserDefaultAddress({ address }));
            } else {
              // Handle when userAddress object is empty but not null
              const response: AxiosResponse<JSONAPIAddress> = yield call(
                createApiAddress,
                billingAPIAddressJSON,
              );
              const address = fromJSON(response.data)!;
              yield put(updateUserDefaultAddress({ address }));
            }
          } else {
            // Handle when userAddress is null
            const response: AxiosResponse<JSONAPIAddress> = yield call(
              createApiAddress,
              billingAPIAddressJSON,
            );
            const address = fromJSON(response.data)!;
            yield put(updateUserDefaultAddress({ address }));
          }
        }

        const formattedBillingAddressJSON: Record<string, string> = {
          //Formatted to be handled by Stripe
          name: billingAddress.name,
          address_line1: billingAddress.street1,
          address_city: billingAddress.city,
          address_zip: billingAddress.zip,
        };
        if (billingAddress.state) {
          formattedBillingAddressJSON.state = billingAddress.state;
        }
        if (billingAddress.street2) {
          formattedBillingAddressJSON.address_line2 = billingAddress.street2;
        }
        if (billingAddress.country) {
          formattedBillingAddressJSON.address_country = billingAddress.country.iso2;
        }

        const result: CreateTokenResult = yield call(
          stripeClient.createToken,
          element,
          formattedBillingAddressJSON,
        );

        if (result.error) {
          yield put(stripeActions.createStripeTokenError(elementId, result.error.message));
          put(stripeActions.setStripeFormError(elementId, result.error.message));
        }
        yield put(stripeActions.createStripeTokenSuccess(elementId, result.token));

        yield put(updateCreditCard(elementId, result.token.id, JSON.stringify(billingAddress)));

        // Calls user store to find if user has set a CC
        const stripeAuthorized: boolean = yield selectState<boolean>(isStripeAuthorized);

        if (!stripeAuthorized) {
          yield put(
            gtmActions.sendGTMEvent({
              eventName: SET_FIRST_CREDIT_CARD,
            }),
          );
          if (location.search?.includes('progressMeter')) {
            yield put(sendTrackEvent('Progress Meter - Add Payment Info Completed'));
          }
          yield put(sendTrackEvent('First Set Credit Card Information'));
          trackPaymentMethodUpdate(trackingReference, PAYMENT_METHOD_EVENT.operations.ADDED, {
            stripeCardFingerprint: result.token.card?.fingerprint,
            fundingMethod: result.token.card?.funding,
            creditDebitSelectionValue,
          });
        } else {
          trackPaymentMethodUpdate(trackingReference, PAYMENT_METHOD_EVENT.operations.UPDATED, {
            stripeCardFingerprint: result.token.card?.fingerprint,
            fundingMethod: result.token.card?.funding,
            creditDebitSelectionValue,
          });
        }

        if (analyticsContext) {
          yield put(sendTrackEvent(`${analyticsContext}-succeed-save`));
        }
      } else {
        yield put(
          stripeActions.createStripeTokenError(
            action.payload.elementId,
            'Trying to create a token for a non-existing element',
          ),
        );
      }
    } else {
      yield put(
        stripeActions.createStripeTokenError(
          action.payload.elementId,
          'Trying to create a token when stripe is not loaded',
        ),
      );
    }
  } catch (e) {
    if (axios.isAxiosError(e) && e.response) {
      const { response } = e;
      requestUpdatedTrack(trackingReference, {
        url: response.config.url || '',
        isError: response.status >= 300,
        status: response.status,
        message: e.message,
      });
    }
    yield put(stripeActions.createStripeTokenError(elementId, e.toString()));
    yield put(stripeActions.cancelStripeFromSubmission(elementId));
    if (e.response) {
      //This will catch Address Validation Errors
      const responseData = e.response.data;
      const errorMessage =
        typeof responseData === 'object'
          ? Object.values(responseData).join('\n')
          : Array.isArray(responseData)
            ? responseData.join('\n')
            : responseData;
      yield put(setPaymentProcessingError(errorMessage));
    }
  }
}

export function* watchStripeChannel() {
  while (true) {
    // This looks like it's just going to take the first dispatched action coming from the Stripe channel?
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const action: Action<any> = yield take(stripeChannel);
    yield put(action);
  }
}

export default [
  watchStripeChannel(),
  takeLatest(ActionKeys.STRIPE_LOAD, loadStripe),
  takeLatest(ActionKeys.STRIPE_CREATE_ELEMENT, createElement),
  takeLatest(ActionKeys.STRIPE_CREATE_TOKEN, createToken),
];
